import { type SARadioStorage, planLabel } from '@/mocks/superAdminStorage';

interface RadioStorageDetailModalProps {
  radio: SARadioStorage | null;
  onClose: () => void;
}

export default function RadioStorageDetailModal({ radio, onClose }: RadioStorageDetailModalProps) {
  if (!radio) return null;

  const pct = Math.round((radio.storageUsedGB / radio.storageTotalGB) * 100);
  const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  const planColors: Record<string, string> = {
    free: 'bg-secondary-100 text-secondary-700',
    pro: 'bg-primary-100 text-primary-700',
    premium: 'bg-accent-100 text-accent-700',
  };

  const types = [
    { label: 'Videos', value: radio.videosGB, icon: 'ri-movie-line', color: 'text-rose-500', barColor: 'bg-rose-400' },
    { label: 'Fotos', value: radio.fotosGB, icon: 'ri-image-line', color: 'text-amber-500', barColor: 'bg-amber-400' },
    { label: 'Audios', value: radio.audiosGB, icon: 'ri-music-line', color: 'text-emerald-500', barColor: 'bg-emerald-400' },
  ];

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background-50 rounded-t-2xl flex items-center justify-between px-5 py-4 border-b border-background-200 z-10">
          <div className="flex items-center gap-3">
            <img src={radio.logoUrl} alt={radio.name} className="w-10 h-10 rounded-xl object-cover shrink-0" />
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">{radio.name}</h3>
              <p className="text-foreground-400 text-[10px]">{radio.frequency} · {radio.city}, {radio.country}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          {/* Plan */}
          <span className={`inline-flex px-2.5 py-1 rounded-md text-[10px] font-semibold ${planColors[radio.plan]}`}>
            {planLabel[radio.plan]} · {radio.storageTotalGB} GB
          </span>

          {/* Storage utilizado / disponible */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-foreground-500 text-[11px] font-semibold">Almacenamiento</span>
              <span className={`text-xs font-bold ${pct >= 90 ? 'text-red-500' : pct >= 70 ? 'text-amber-500' : 'text-emerald-500'}`}>
                {radio.storageUsedGB} / {radio.storageTotalGB} GB ({pct}%)
              </span>
            </div>
            <div className="relative w-full h-3 rounded-full bg-background-200 overflow-hidden">
              <div className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }}></div>
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-background-200"></div>

          {/* Desglose por tipo */}
          <div>
            <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-3">Desglose por tipo</p>
            <div className="space-y-3">
              {types.map((t) => {
                const typePct = radio.storageUsedGB > 0 ? (t.value / radio.storageUsedGB) * 100 : 0;
                return (
                  <div key={t.label} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
                      <i className={`${t.icon} ${t.color} text-sm`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-foreground-600 text-[11px] font-medium">{t.label}</span>
                        <span className="text-foreground-800 text-[11px] font-bold">{t.value.toFixed(2)} GB</span>
                      </div>
                      <div className="relative w-full h-2 rounded-full bg-background-200 overflow-hidden">
                        <div className={`absolute inset-y-0 left-0 rounded-full ${t.barColor}`} style={{ width: `${Math.min(typePct, 100)}%` }}></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}